import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "../redux/store";

export interface FeedbackDraft {
  stars: number;
  usabilityRating: string;
  navigationEase: string;
  comment: string;
}

interface PortalFeedbackContextType {
  isOpen: boolean;
  isMinimized: boolean;
  hasSubmitted: boolean;
  draft: FeedbackDraft;
  setDraft: React.Dispatch<React.SetStateAction<FeedbackDraft>>;
  openFeedback: () => void;
  minimizeFeedback: () => void;
  closeFeedback: () => void;
  markSubmitted: () => void;
  resetFeedback: () => void;
}

const STORAGE_DISMISSED_KEY = "dealer_survey_dismissed";
const AUTO_TRIGGER_DELAY_MS = 90 * 1000; // 90 seconds

const initialDraft: FeedbackDraft = {
  stars: 0,
  usabilityRating: "",
  navigationEase: "",
  comment: "",
};

const PortalFeedbackContext = createContext<PortalFeedbackContextType | undefined>(undefined);

export const PortalFeedbackProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { profile } = useSelector((state: RootState) => state.dealer);

  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [draft, setDraft] = useState<FeedbackDraft>(initialDraft);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up legacy keys from localStorage once so they don't block new logic
  useEffect(() => {
    try {
      localStorage.removeItem("deco_dealer_feedback_submitted");
      localStorage.removeItem("deco_dealer_feedback_minimized");
    } catch {
      // ignore
    }
  }, []);

  // Sync submission state directly from profile API
  useEffect(() => {
    if (profile?.isSurveySubmitted) {
      setHasSubmitted(true);
    } else {
      setHasSubmitted(false);
    }
  }, [profile?.isSurveySubmitted]);

  // 90-second session trigger
  useEffect(() => {
    // 1. If not logged in or modal is already open, do nothing
    if (!user || isOpen) return;

    // 2. If backend says survey is already submitted, NEVER auto-popup!
    if (profile?.isSurveySubmitted || hasSubmitted) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    // 3. If user closed/dismissed popup without submitting in this session, do not auto-popup
    try {
      const isDismissed = localStorage.getItem(STORAGE_DISMISSED_KEY);
      if (isDismissed === "true") {
        return;
      }
    } catch {
      // ignore
    }

    // 4. Start 90s timer
    timerRef.current = setTimeout(() => {
      // Double check dismissal and submission state right before opening
      try {
        const isDismissed = localStorage.getItem(STORAGE_DISMISSED_KEY);
        if (isDismissed === "true") return;
      } catch {}

      if (profile?.isSurveySubmitted) return;

      setIsOpen(true);
      setIsMinimized(false);
    }, AUTO_TRIGGER_DELAY_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [user, isOpen, profile?.isSurveySubmitted, hasSubmitted]);

  const openFeedback = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setIsMinimized(false);
    setIsOpen(true);
  }, []);

  const minimizeFeedback = useCallback(() => {
    setIsOpen(false);
    setIsMinimized(true);
    try {
      localStorage.setItem(STORAGE_DISMISSED_KEY, "true");
    } catch {
      // ignore
    }
  }, []);

  const closeFeedback = useCallback(() => {
    setIsOpen(false);
    setIsMinimized(false);
    try {
      // User closed popup without submitting: record dismissal so it doesn't re-popup after 90 seconds
      localStorage.setItem(STORAGE_DISMISSED_KEY, "true");
    } catch {
      // ignore
    }
  }, []);

  const markSubmitted = useCallback(() => {
    setIsOpen(false);
    setIsMinimized(false);
    setHasSubmitted(true);
    try {
      localStorage.setItem(STORAGE_DISMISSED_KEY, "true");
    } catch {
      // ignore
    }
  }, []);

  const resetFeedback = useCallback(() => {
    setDraft(initialDraft);
    setIsOpen(false);
    setIsMinimized(false);
    setHasSubmitted(false);
    try {
      localStorage.removeItem(STORAGE_DISMISSED_KEY);
    } catch {
      // ignore
    }
  }, []);

  return (
    <PortalFeedbackContext.Provider
      value={{
        isOpen,
        isMinimized,
        hasSubmitted,
        draft,
        setDraft,
        openFeedback,
        minimizeFeedback,
        closeFeedback,
        markSubmitted,
        resetFeedback,
      }}
    >
      {children}
    </PortalFeedbackContext.Provider>
  );
};

export const usePortalFeedback = () => {
  const context = useContext(PortalFeedbackContext);
  if (!context) {
    throw new Error("usePortalFeedback must be used within a PortalFeedbackProvider");
  }
  return context;
};
